import {useEffect, useCallback, useMemo, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {toast} from 'react-toastify'

import {
  clearStates,
  setCountrySelectState,
  setSelectedCountryStates,
  setTransactionTypeState,
  initializeParents,
  setActiveAccordion,
  addChildAccount,
  updateParentField,
  updateChildField,
  setParentErrors,
  setChildErrors,
  setParentSaving,
  setChildSaving,
  setParentSaved,
  setChildSaved,
  updateCountryInTree,
  selectEmailLookupState,
  selectTransactionTypeState,
  selectCountrySelectState,
  selectParents,
  selectActiveAccordion,
  setParentFieldErrors,
  setChildFieldErrors,
  deleteChildAccount,
} from '../../../../app-store/AccountSetupSlice'

import {emptyLocation} from '../config/locationFields'
import {getTaxIdentifier} from '../utils/taxIdentifier'

import {
  getFieldFromErrorMessage,
  validateField,
  validateLocation,
} from '../utils/formHelpers'
import {
  getFirstChildPayloadBillTo,
  toSingleLocationPayloadBillTo,
  toLocationPayloadBillTo,
} from '../utils/billTo'

import countryRegionJSON from '../../../../utils/country-region.json'

import {
  fetchSelectedCountryRegionList,
  validateAddressAndTZ,
} from '../services/locationAPI'
import {buildFieldConfigMap, PARTNER_VALUES} from '../config/locationFieldRules'
import {useFlags} from 'launchdarkly-react-client-sdk'
import {selectUserRoles} from '../../../../app-store/selector'
import {useUser} from '../../../../context/user-context'
import {useUserFeatures} from '../../../../context/user-features-context'
import {useAddressSuggestionPrompt} from './useAddressSuggestionPrompt'

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

// Only PartnerOps defaults to Keap Partner — Admin and TSS also get the Keap
// Partner/Legacy Partner toggle options (see KEAP_LEGACY_PARTNER_ACCESS in
// locationFieldRules.js) but keep the plain 'no' default like every other role.
const hasPartnerOpsRole = roles => {
  const roleList = Array.isArray(roles) ? roles : roles ? [roles] : []
  return roleList.some(role => String(role).toLowerCase() === 'partnerops')
}

// Normalize a network id to the networkid EMAIL form (e.g. govenm01 -> govenm01@thryv.com).
// Pass through anything already email-shaped; return null for empty input.
const toNetworkEmail = value => {
  if (!value) return null
  const v = String(value).trim()
  if (!v) return null
  return v.includes('@') ? v : `${v}@thryv.com`
}

// Address fields (street/city/state/postCode) are free-typed and validateLocation only checks
// a trimmed copy for format — it never normalizes the value actually stored in form state. A
// leading space or a doubled internal space (e.g. Canadian postal code "M4A  2M5") survives all
// the way into the intg-signup-service payload. Collapse whitespace runs and trim before sending.
const normalizeAddressValue = value => {
  if (value === null || value === undefined) return value
  return String(value).trim().replace(/\s+/g, ' ')
}

// Fixed rep-attribution identities for partner-channel roles — mirror of the backend
// resolveSalesRepIdentity constants in ooe-service/routes/multi-location-route.js.
const KEAP_PARTNER_SALES_REP_ID = 'fict997362@thryv.com'
const RESELLER_SALES_REP_ID = 'fict996814@thryv.com'

// KeapPartner/InfluencerOps/PartnerOps are all Keap partner-channel roles and attribute to
// the same fixed Keap partner rep id — mirror of the backend KEAP_PARTNER_CHANNEL_ROLES.
const KEAP_PARTNER_CHANNEL_ROLES = [
  'KeapPartner',
  'InfluencerOps',
  'PartnerOps',
]

// Client-side mirror of the backend resolveSalesRepIdentity (multi-location-route.js). The
// backend is authoritative — it recomputes both salesRepId and on_behalf_of server-side and
// strips any client-supplied onBehalf — so this is defense-in-depth only. Both salesRepId and
// onBehalf on the request body carry the same role-derived value, and a client-supplied
// selection is never honored for any role (dropped 2026-07-07, including for PremisePilot):
//   1. KeapPartner/InfluencerOps/PartnerOps → fixed Keap partner rep id
//   2. Reseller    → fixed reseller rep id
//   3. everything else (including PremisePilot) → the logged-in rep's networkid email
const resolveSalesRepIdentity = (role, currentUserNetworkEmail) => {
  const primaryRole = Array.isArray(role) ? role[0] : role
  if (KEAP_PARTNER_CHANNEL_ROLES.includes(primaryRole))
    return KEAP_PARTNER_SALES_REP_ID
  if (primaryRole === 'Reseller') return RESELLER_SALES_REP_ID
  return currentUserNetworkEmail || null
}

const createAccount = (type = 'parent', verifiedEmail, extra = {}) => {
  const {values: extraValues = {}, ...rest} = extra

  return {
    id: crypto.randomUUID(),

    type,

    values: {...emptyLocation(verifiedEmail), ...extraValues},

    errors: {},
    touched: {},

    dirty: false,
    saving: false,
    saved: false,
    existing: false,

    children: [],

    autoCreated: false,

    ...rest,
  }
}

const buildInitialAccounts = (verifiedEmail, defaultValues = {}) => {
  const parent = createAccount('parent', verifiedEmail, {
    values: defaultValues,
  })

  parent.children = [
    createAccount('child', verifiedEmail, {
      autoCreated: true,
      values: defaultValues,
    }),
  ]

  return [parent]
}

const stampCountry = (account, country) => {
  if (!country) return account

  return {
    ...account,
    country,
    values: {
      ...account.values,
      country,
    },
    children:
      account.children?.map(child => stampCountry(child, country)) ?? [],
  }
}

const getDefaultChild = parent => parent?.children?.[0]

// ─────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────

export function useAccountTree(run, client) {
  const dispatch = useDispatch()

  // ─────────────────────────────────────────
  // Selectors
  // ─────────────────────────────────────────

  const {frontendOverrideSst} = useFlags()
  const {Enable_Internal_Account_Checkbox} = useUserFeatures()

  const {actualRole} = useSelector(selectUserRoles)

  // Default salesRepId for a self-sale = the logged-in rep's networkid email. Use the
  // network_id claim (the network username + domain, e.g. vutukj01@thryv.com) — NOT
  // patchedUser.email, which is the Azure AD display-name mail (First.Last@thryv.com).
  const {user} = useUser()
  const currentUserNetworkEmail =
    toNetworkEmail(user?.network_id) || toNetworkEmail(user?.email) || null

  const {verifiedEmail} = useSelector(selectEmailLookupState)

  const {selectedTransactionType} = useSelector(selectTransactionTypeState)

  const {selectedCountry, states} = useSelector(selectCountrySelectState)

  const parents = useSelector(selectParents)

  const activeAccordion = useSelector(selectActiveAccordion)

  const isMultiLocation = selectedTransactionType === 'multiple-locations'

  const [isAddressValidating, setIsAddressValidating] = useState(false)
  const {addressSuggestion, promptForConfirmation, resolveAddressSuggestion} =
    useAddressSuggestionPrompt()
  const partnerOpsFieldDefaults = useMemo(
    () =>
      hasPartnerOpsRole(actualRole) ? {salesRepOrPartner: 'keap-partner'} : {},
    [actualRole],
  )

  // ─────────────────────────────────────────
  // Initialize
  // ─────────────────────────────────────────

  useEffect(() => {
    if (parents?.length) return

    const initialParents = buildInitialAccounts(
      verifiedEmail,
      partnerOpsFieldDefaults,
    ).map(parent => stampCountry(parent, selectedCountry))

    dispatch(initializeParents(initialParents))

    dispatch(setActiveAccordion(initialParents?.[0]?.id))
  }, [
    parents,
    verifiedEmail,
    selectedCountry,
    partnerOpsFieldDefaults,
    dispatch,
  ])

  // ─────────────────────────────────────────────
  // Field Config Helper
  // ─────────────────────────────────────────────

  const getFieldConfigMap = useCallback(
    account => {
      return buildFieldConfigMap({
        account,
        roles: actualRole,
        values: account.values,
        selectedCountry,
        selectedTransactionType,
        states,
        frontendOverrideSst,
        enableInternalAccountCheckbox: Enable_Internal_Account_Checkbox,
      })
    },
    [
      actualRole,
      selectedCountry,
      selectedTransactionType,
      states,
      frontendOverrideSst,
      Enable_Internal_Account_Checkbox,
    ],
  )

  // ─────────────────────────────────────────
  // Parent Updates
  // ─────────────────────────────────────────

  const validateChildField = useCallback(
    (child, parentId, childId, field, value) => {
      const fieldConfigMap = getFieldConfigMap(child)

      const errors = validateField(child, value, field, fieldConfigMap)

      dispatch(
        setChildFieldErrors({
          parentId,
          childId,
          field,
          errors,
        }),
      )
    },
    [dispatch, getFieldConfigMap],
  )
  const validateParentField = useCallback(
    (parent, parentId, field, value) => {
      const fieldConfigMap = getFieldConfigMap(parent)

      const errors = validateField(parent, value, field, fieldConfigMap)

      dispatch(
        setParentFieldErrors({
          parentId,
          field,
          errors,
        }),
      )
    },
    [dispatch, getFieldConfigMap],
  )

  const updateParent = useCallback(
    (parent, parentId, field, value) => {
      dispatch(
        updateParentField({
          parentId,
          field,
          value,
        }),
      )

      validateParentField(parent, parentId, field, value)
    },
    [dispatch, validateParentField],
  )

  // ─────────────────────────────────────────
  // Child Updates
  // ─────────────────────────────────────────

  const updateChild = useCallback(
    (child, parentId, childId, field, value) => {
      dispatch(
        updateChildField({
          parentId,
          childId,
          field,
          value,
        }),
      )

      validateChildField(child, parentId, childId, field, value)
    },
    [dispatch, validateChildField],
  )

  // ─────────────────────────────────────────
  // Add Child
  // ─────────────────────────────────────────

  const addChild = useCallback(
    parentId => {
      const child = createAccount('child', verifiedEmail, {
        values: partnerOpsFieldDefaults,
      })
      // Stamp the currently selected country so validation doesn't fall back
      // to 'US' for non-US countries (CA, NZ, AU, etc.).
      if (selectedCountry) {
        child.values.country = selectedCountry
      }
      dispatch(
        addChildAccount({
          parentId,
          child,
        }),
      )

      dispatch(setActiveAccordion(child.id))

      return child
    },
    [dispatch, verifiedEmail, selectedCountry, partnerOpsFieldDefaults],
  )

  // ─────────────────────────────────────────
  // Delete child Handling
  // ─────────────────────────────────────────

  const discardChild = useCallback(
    (childId, parentId) => {
      dispatch(
        deleteChildAccount({
          parentId,
          childId,
        }),
      )
    },
    [dispatch],
  )
  // Re-validate country-sensitive fields when the country changes so stale
  // phone/postal errors from the previous country are cleared or corrected
  // without requiring the user to re-touch each field.
  useEffect(() => {
    if (!selectedCountry || !parents?.length) return

    const COUNTRY_SENSITIVE_FIELDS = ['mobile', 'telephone', 'postCode']

    parents.forEach(parent => {
      const fieldConfigMap = getFieldConfigMap(parent)
      COUNTRY_SENSITIVE_FIELDS.forEach(field => {
        if (parent.values[field]) {
          const errors = validateField(
            parent,
            parent.values[field],
            field,
            fieldConfigMap,
          )
          dispatch(setParentFieldErrors({parentId: parent.id, field, errors}))
        }
      })

      parent.children?.forEach(child => {
        const childFieldConfigMap = getFieldConfigMap(child)
        COUNTRY_SENSITIVE_FIELDS.forEach(field => {
          if (child.values[field]) {
            const errors = validateField(
              child,
              child.values[field],
              field,
              childFieldConfigMap,
            )
            dispatch(
              setChildFieldErrors({
                parentId: parent.id,
                childId: child.id,
                field,
                errors,
              }),
            )
          }
        })
      })
    })
  }, [selectedCountry]) // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────
  // Country Handling
  // ─────────────────────────────────────────

  const loadStates = useCallback(
    country => {
      if (!country) return

      try {
        const states = fetchSelectedCountryRegionList(
          country,
          countryRegionJSON,
        )

        dispatch(setSelectedCountryStates(states))
      } catch {
        dispatch(clearStates())
      }
    },
    [dispatch],
  )

  const handleCountryandRegionChange = useCallback(
    country => {
      loadStates(country)

      dispatch(setCountrySelectState(country))

      dispatch(updateCountryInTree(country))
    },
    [dispatch, loadStates],
  )

  // ─────────────────────────────────────────
  // Transaction Type
  // ─────────────────────────────────────────

  const handleTransactionTypeChange = useCallback(
    transactionType => {
      dispatch(setTransactionTypeState(transactionType))

      const updatedParents = buildInitialAccounts(
        verifiedEmail,
        partnerOpsFieldDefaults,
      )

      dispatch(initializeParents(updatedParents))

      dispatch(setActiveAccordion(updatedParents?.[0]?.id))

      if (selectedCountry) {
        dispatch(updateCountryInTree(selectedCountry))
      }
    },
    [dispatch, verifiedEmail, selectedCountry, partnerOpsFieldDefaults],
  )

  const setAddressFieldError = useCallback(
    ({parentId, childId, isChild, field, message}) => {
      const payload = {
        field,
        errors: {
          [field]: message,
        },
      }

      if (isChild) {
        dispatch(
          setChildFieldErrors({
            parentId,
            childId,
            ...payload,
          }),
        )
      } else {
        dispatch(
          setParentFieldErrors({
            parentId,
            ...payload,
          }),
        )
      }
    },
    [dispatch],
  )

  const handleAddressValidation = useCallback(
    async ({account, parentId, childId = null, isChild = false}) => {
      const {address, city, state, postCode} = account.values
      const country = account.values.country || selectedCountry

      if (!city || !state || !postCode || !country) {
        return {
          success: false,
          error: 'Missing address fields',
        }
      }
      setIsAddressValidating(true)
      try {
        const response = await validateAddressAndTZ(run, client, {
          address,
          city,
          state,
          zipCode: postCode,
          country,
        })

        const {timeZone, status, original, recommended, message} = response
        const updateAction = isChild ? updateChildField : updateParentField

        // What actually lands in the payload builders below — they're handed a stale,
        // closure-captured copy of `account.values` from before this function's dispatches
        // run, so they can't see the corrected fields via the store. Report them explicitly.
        const resolvedAddress = {address, city, state, postCode}

        if (status === 'SUGGESTED') {
          const choice = await promptForConfirmation({
            original,
            recommended: {...recommended, lat: response.lat, lng: response.lng},
            message,
          })

          if (choice === 'cancel') {
            setIsAddressValidating(false)
            return {
              success: false,
              error: 'Address not confirmed',
            }
          }

          if (choice === 'recommended') {
            const trim = v => (typeof v === 'string' ? v.trim() : v)
            if (recommended.address) {
              resolvedAddress.address = trim(recommended.address)
              dispatch(
                updateAction({
                  parentId,
                  ...(isChild && {childId}),
                  field: 'address',
                  value: trim(recommended.address),
                }),
              )
            }
            resolvedAddress.city = trim(recommended.city)
            dispatch(
              updateAction({
                parentId,
                ...(isChild && {childId}),
                field: 'city',
                value: trim(recommended.city),
              }),
            )
            resolvedAddress.state = trim(recommended.state)
            dispatch(
              updateAction({
                parentId,
                ...(isChild && {childId}),
                field: 'state',
                value: trim(recommended.state),
              }),
            )
            resolvedAddress.postCode = trim(recommended.zipCode)
            dispatch(
              updateAction({
                parentId,
                ...(isChild && {childId}),
                field: 'postCode',
                value: trim(recommended.zipCode),
              }),
            )
          }
        }

        if (timeZone) {
          dispatch(
            updateAction({
              parentId,
              ...(isChild && {childId}),
              field: 'timeZone',
              value: timeZone,
            }),
          )
        }
        setIsAddressValidating(false)

        return {
          success: true,
          timeZone,
          resolvedAddress,
        }
      } catch (error) {
        const message = error?.message || 'Address validation failed'

        const status = error?.status

        toast.error(message)

        const field = status === 422 ? getFieldFromErrorMessage(message) : null

        if (field) {
          setAddressFieldError({
            parentId,
            childId,
            isChild,
            field,
            message,
          })
        }
        setIsAddressValidating(false)

        return {
          success: false,
          error: message,
        }
      }
    },
    [
      dispatch,
      run,
      client,
      selectedCountry,
      setAddressFieldError,
      promptForConfirmation,
    ],
  )

  // ─────────────────────────────────────────
  // Payload Mappers
  // ─────────────────────────────────────────

  function mapSingleLocParentToPayload(values, timeZone) {
    const phone = values.telephone || values.mobile || null
    const country = values.country || selectedCountry || null

    const isPartner = PARTNER_VALUES.includes(values.salesRepOrPartner)

    const salesRepChannel = Array.isArray(actualRole)
      ? actualRole[0] || null
      : actualRole || null

    return {
      email: values.email || null,
      firstName: values.firstName || null,
      lastName: values.lastName || null,
      company: values.businessName || null,
      phone,

      country,
      // AU/NZ-only field, collected by locationFields.js's 'abn' entry but never
      // forwarded to the backend — Recurly's Account.vatNumber stayed permanently
      // null despite the rep entering it, forcing a re-entry later on Edit/Create
      // Purchase Link. See multi-location-parent-service.js's applyAbnIfNeeded.
      abn: getTaxIdentifier(values) || null,

      timeZone: timeZone || values.timeZone || 'America/Chicago',

      address: {
        street: normalizeAddressValue(values.address) || null,
        city: normalizeAddressValue(values.city) || null,
        state: normalizeAddressValue(values.state) || null,
        zipCode: normalizeAddressValue(values.postCode) || null,
        country,
        phone,
      },

      sfdcContactEmail: values.salesforceEmail || null,
      auth0UserId: values.auth0UserId || null,
      salesRepChannel,
      salesRepOrPartner: values.salesRepOrPartner || null,

      salesRepId: resolveSalesRepIdentity(actualRole, currentUserNetworkEmail),
      onBehalf: resolveSalesRepIdentity(actualRole, currentUserNetworkEmail),

      ...(isPartner && {
        xpCode: values.xpCode,
      }),

      // FULSAS-11921: pass checkbox value so backend stamps Recurly on creation.
      internalAccount: values.internalAccount === true,
    }
  }

  const mapSingleLocChildToPayload = (
    parentPayload,
    parentResponse,
    parentValues,
  ) => {
    const phone = parentPayload.phone || null
    const country = parentPayload.country || selectedCountry || null

    const isPartner = PARTNER_VALUES.includes(parentPayload.salesRepOrPartner)

    const salesRepChannel = Array.isArray(actualRole)
      ? actualRole[0] || null
      : actualRole || null

    return {
      parentOrgId: parentResponse?.parentOrgId,
      parentSignupId: parentResponse?.signupId,
      parentCcId: parentResponse?.ccId,
      parentRecurlyAccountId: parentResponse?.recurlyAccountId,
      provisionRecordId: parentResponse?.provisionRecordId,

      email: parentPayload.email || null,
      firstName: parentPayload.firstName || null,
      lastName: parentPayload.lastName || null,

      company: parentPayload.company || null,

      phone,
      country,
      abn: parentPayload.abn || null,

      timeZone: parentPayload.timeZone || 'America/Chicago',

      address: {
        street: normalizeAddressValue(parentPayload.address?.street) || null,
        city: normalizeAddressValue(parentPayload.address?.city) || null,
        state: normalizeAddressValue(parentPayload.address?.state) || null,
        zipCode: normalizeAddressValue(parentPayload.address?.zipCode) || null,
        country,
        phone,
      },

      sfdcContactEmail: parentPayload.sfdcContactEmail || null,
      auth0UserId: parentPayload.auth0UserId || null,

      locationName: parentValues?.locationName || null,
      billTo: toSingleLocationPayloadBillTo(),

      salesRepChannel,
      salesRepOrPartner: parentPayload.salesRepOrPartner || null,

      // Inherit the parent's already-resolved rep-attribution identity; fall back to the
      // logged-in rep so the child never provisions with a blank salesRepId. onBehalf mirrors
      // salesRepId (both carry the same value; backend is authoritative).
      salesRepId: parentPayload.salesRepId || currentUserNetworkEmail || null,
      onBehalf:
        parentPayload.onBehalf ||
        parentPayload.salesRepId ||
        currentUserNetworkEmail ||
        null,

      internalAccount: parentPayload.internalAccount === true,

      ...(isPartner && {
        xpCode: parentPayload.xpCode,
      }),
    }
  }

  // ─────────────────────────────────────────
  // Saving Helpers
  // ─────────────────────────────────────────

  const setParentSavingState = (parentId, saving) => {
    dispatch(
      setParentSaving({
        parentId,
        saving,
      }),
    )
  }

  const setChildSavingState = (parentId, childId, saving) => {
    dispatch(
      setChildSaving({
        parentId,
        childId,
        saving,
      }),
    )
  }

  // ─────────────────────────────────────────
  // Save Child
  // ─────────────────────────────────────────

  const saveSingleLocChild = async ({
    parent,
    parentPayload,
    parentResponse,
  }) => {
    const child = getDefaultChild(parent)

    if (!child) {
      return {
        success: false,
      }
    }

    try {
      setChildSavingState(parent.id, child.id, true)

      const childPayload = mapSingleLocChildToPayload(
        parentPayload,
        parentResponse,
        parent.values,
      )

      const childResponse = await run(
        client('multi-location/location', {
          method: 'POST',
          data: childPayload,
        }),
      )
      // if (!childResponse?.thryvId && !childResponse?.ccId) {
      //   toast.error('Location response missing required ccId')
      //   throw new Error('Location ID missing in response')
      // }
      // if (childResponse?.thryvId || childResponse?.ccId) {
      // toast.success('Location saved successfully')

      dispatch(
        setChildSaved({
          parentId: parent.id,

          childId: child.id,

          data: {
            values: parent.values,

            response: childResponse,

            saved: true,

            dirty: false,

            existing: true,
          },
        }),
      )

      return {
        success: true,
        childResponse,
      }
      // }
    } catch (error) {
      console.error(error)

      return {
        success: false,
        error,
      }
    } finally {
      setChildSavingState(parent.id, child.id, false)
    }
  }

  // ─────────────────────────────────────────
  // Save Parent
  // ─────────────────────────────────────────

  const saveSingleLocParent = async () => {
    const parent = parents?.[0]

    if (!parent) {
      return {
        success: false,
      }
    }

    const parentId = parent.id

    try {
      // validate all field validations
      const fieldConfigMap = getFieldConfigMap(parent)

      const errors = validateLocation(parent.values, fieldConfigMap)

      if (Object.keys(errors).length) {
        dispatch(
          setParentErrors({
            parentId,
            errors,
          }),
        )

        toast.error('Please complete all required fields before continuing.')

        return {
          success: false,
          errors,
        }
      }

      const addressResult = await handleAddressValidation({
        account: parent,
        parentId,
        childId: null,
        isChild: false,
      })

      if (!addressResult.success) {
        return addressResult
      }

      setParentSavingState(parentId, true)

      const parentPayload = mapSingleLocParentToPayload(
        {...parent.values, ...addressResult.resolvedAddress},
        addressResult.timeZone,
      )

      const parentResponse = await run(
        client('multi-location/parent', {
          method: 'POST',
          data: parentPayload,
        }),
      )

      // if (!parentResponse?.ccId && !parentResponse?.parentOrgId) {
      //   toast.error('Parent response missing required ccId')
      //   throw new Error('Parent response missing required ID')
      // }
      // if (parentResponse?.ccId || parentResponse?.parentOrgId) {
      // toast.success('Parent saved successfully')

      dispatch(
        setParentSaved({
          parentId,

          data: {
            response: parentResponse,
            saved: true,

            dirty: false,

            existing: true,

            // parentOrgId: parentResponse?.parentOrgId,

            // parentSignupId: parentResponse?.parentSignupId,

            // parentCcId: parentResponse?.ccId,

            // parentRecurlyAccountId: parentResponse?.parentRecurlyAccountId,

            // provisionRecordId: parentResponse?.provisionRecordId,
          },
        }),
      )

      const childResponse = await saveSingleLocChild({
        parent,

        parentPayload,

        parentResponse,
      })

      return {
        success: true,

        parentResponse,

        childResponse,
      }
      // }
    } catch (error) {
      console.error(error)

      toast.error('Failed to save locations')

      return {
        success: false,
        error,
      }
    } finally {
      setParentSavingState(parentId, false)
    }
  }

  // ─────────────────────────────────────────
  // Multilocation Save
  // ─────────────────────────────────────────

  // ─────────────────────────────────────────
  // Payload Mappers
  // ─────────────────────────────────────────

  function mapParentToPayload(parent, timeZone) {
    const phone = parent.mobile || null
    const country = parent.country || selectedCountry || null

    const isPartner = PARTNER_VALUES.includes(parent.salesRepOrPartner)

    const salesRepChannel = Array.isArray(actualRole)
      ? actualRole[0] || null
      : actualRole || null

    return {
      email: parent.email || null,
      firstName: parent.firstName || null,
      lastName: parent.lastName || null,
      company: parent.businessName || null,

      phone,
      country,
      abn: getTaxIdentifier(parent) || null,

      timeZone: timeZone || parent.timeZone || 'America/Chicago',

      address: {
        street: normalizeAddressValue(parent.address) || null,
        city: normalizeAddressValue(parent.city) || null,
        state: normalizeAddressValue(parent.state) || null,
        zipCode: normalizeAddressValue(parent.postCode) || null,
        country,
        phone,
      },

      sfdcContactEmail: parent.salesforceEmail || null,
      auth0UserId: parent.auth0UserId || null,

      salesRepChannel,
      salesRepOrPartner: parent.salesRepOrPartner || null,

      salesRepId: resolveSalesRepIdentity(actualRole, currentUserNetworkEmail),
      onBehalf: resolveSalesRepIdentity(actualRole, currentUserNetworkEmail),

      ...(isPartner && {
        xpCode: parent.xpCode,
      }),

      // FULSAS-11921: pass checkbox value so backend stamps Recurly on creation.
      internalAccount: parent.internalAccount === true,
    }
  }

  function mapChildToPayload(child, parent, timeZone) {
    const phone = child.telephone || child.mobile || null
    const country = parent.country || selectedCountry || null
    const isPartner = PARTNER_VALUES.includes(child.salesRepOrPartner)

    const salesRepChannel = Array.isArray(actualRole)
      ? actualRole[0] || null
      : actualRole || null

    return {
      parentOrgId: parent?.response?.parentOrgId,
      parentSignupId: parent?.response?.signupId,
      parentCcId: parent?.response?.ccId,
      parentRecurlyAccountId: parent?.response?.recurlyAccountId,
      provisionRecordId: parent?.response?.provisionRecordId,

      email: child.email || null,
      firstName: child.firstName || null,
      lastName: child.lastName || null,

      company: child.businessName || null,

      phone,
      country,
      abn: getTaxIdentifier(child) || null,

      timeZone: timeZone || child.timeZone || 'America/Chicago',

      address: {
        street: normalizeAddressValue(child.address) || null,
        city: normalizeAddressValue(child.city) || null,
        state: normalizeAddressValue(child.state) || null,
        zipCode: normalizeAddressValue(child.postCode) || null,
        country,
        phone,
      },

      sfdcContactEmail: child.salesforceEmail || null,
      salesRepChannel,
      salesRepOrPartner: child.salesRepOrPartner || null,
      auth0UserId: child.auth0UserId || null,

      locationName: child.locationName || null,

      billTo: toLocationPayloadBillTo(child.billTo),

      salesRepId: resolveSalesRepIdentity(actualRole, currentUserNetworkEmail),
      onBehalf: resolveSalesRepIdentity(actualRole, currentUserNetworkEmail),

      internalAccount: parent?.values?.internalAccount === true,

      ...(isPartner && {
        xpCode: child.xpCode,
      }),
    }
  }

  function mapFirstChildToPayload(child, parent, timeZone, parentValues) {
    const phone = child.telephone || child.mobile || null
    const country = child.country || selectedCountry || null

    const isPartner = PARTNER_VALUES.includes(child.salesRepOrPartner)

    const salesRepChannel = Array.isArray(actualRole)
      ? actualRole[0] || null
      : actualRole || null

    return {
      parentOrgId: parent?.parentOrgId,
      parentSignupId: parent?.signupId,
      parentCcId: parent?.ccId,
      parentRecurlyAccountId: parent?.recurlyAccountId,
      provisionRecordId: parent?.provisionRecordId,

      email: child.email || null,
      firstName: child.firstName || null,
      lastName: child.lastName || null,

      company: child.businessName || null,

      phone,
      country,
      abn: getTaxIdentifier(child) || null,

      timeZone: timeZone || child.timeZone || 'America/Chicago',

      address: {
        street: normalizeAddressValue(child.address) || null,
        city: normalizeAddressValue(child.city) || null,
        state: normalizeAddressValue(child.state) || null,
        zipCode: normalizeAddressValue(child.postCode) || null,
        country,
        phone,
      },

      sfdcContactEmail: child.salesforceEmail || null,
      auth0UserId: child.auth0UserId || null,

      locationName: child.locationName || null,

      billTo: getFirstChildPayloadBillTo(child, parentValues),

      salesRepChannel,
      salesRepOrPartner: child.salesRepOrPartner || null,

      salesRepId: resolveSalesRepIdentity(actualRole, currentUserNetworkEmail),
      onBehalf: resolveSalesRepIdentity(actualRole, currentUserNetworkEmail),

      internalAccount: parentValues?.internalAccount === true,

      ...(isPartner && {
        xpCode: child.xpCode,
      }),
    }
  }

  // ─────────────────────────────────────────
  // Save Parent
  // ─────────────────────────────────────────

  // Dead/superseded by saveSingleLocParent + saveParentWithFirstChild (not returned
  // from this hook). Kept for now; eslint-disabled so the build compiles.
  // eslint-disable-next-line no-unused-vars
  const saveParent = async parentId => {
    const parent = parents.find(p => p.id === parentId)

    if (!parent) return

    const fieldConfigMap = getFieldConfigMap(parent)

    const errors = validateLocation(parent.values, fieldConfigMap)

    if (Object.keys(errors).length) {
      dispatch(
        setParentErrors({
          parentId,
          errors,
        }),
      )

      return
    }

    try {
      dispatch(
        setParentSaving({
          parentId,
          saving: true,
        }),
      )

      const payload = mapParentToPayload(parent.values)

      // const response = await saveParentAccount(payload)
      const parentResponse = await run(
        client('multi-location/parent', {
          method: 'POST',
          data: payload,
        }),
      )

      // if (!parentResponse?.ccId) {
      //   toast.error('Parent response missing required ccId')
      //   throw new Error('Parent ID missing in response')
      // }

      // if (parentResponse.ccId) {
      // toast.success('Parent saved successfully')

      dispatch(
        setParentSaved({
          parentId,
          data: {
            saved: true,

            dirty: false,

            existing: true,

            parentOrgId: parentResponse?.parentOrgId,

            parentSignupId: parentResponse?.parentSignupId,

            parentCcId: parentResponse?.parentCcId,

            parentRecurlyAccountId: parentResponse?.parentRecurlyAccountId,

            provisionRecordId: parentResponse?.provisionRecordId,
          },
        }),
      )

      return {
        success: true,
        parentResponse,
      }
      // }
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(
        setParentSaving({
          parentId,
          saving: false,
        }),
      )
    }
  }

  const saveParentWithFirstChild = async parent => {
    // const parent = parents?.[0]

    if (!parent) {
      return {
        success: false,
        error: 'Parent not found',
      }
    }

    const child = parent?.children?.[0]

    if (!child) {
      return {
        success: false,
        error: 'First child not found',
      }
    }

    const parentId = parent.id
    const childId = child.id

    try {
      // ─────────────────────────────
      // Validation (Parent + Child)
      // ─────────────────────────────

      const parentFieldConfigMap = getFieldConfigMap(parent)

      const childFieldConfigMap = getFieldConfigMap(child)

      const parentErrors = validateLocation(parent.values, parentFieldConfigMap)

      const childErrors = validateLocation(child.values, childFieldConfigMap)

      if (Object.keys(parentErrors).length || Object.keys(childErrors).length) {
        if (Object.keys(parentErrors).length) {
          dispatch(
            setParentErrors({
              parentId,
              errors: parentErrors,
            }),
          )
        }

        if (Object.keys(childErrors).length) {
          dispatch(
            setChildErrors({
              parentId,
              childId,
              errors: childErrors,
            }),
          )
        }

        toast.error('Please complete all required fields before saving.')

        return {
          success: false,
          parentErrors,
          childErrors,
        }
      }

      // validate addres fields -- city, state, and post code and get time zone
      const addressResultParent = await handleAddressValidation({
        account: parent,
        parentId,
        childId: null,
        isChild: false,
      })

      if (!addressResultParent.success) {
        return addressResultParent
      }
      const addressResultChild = await handleAddressValidation({
        account: child,
        parentId,
        childId: childId,
        isChild: true,
      })

      if (!addressResultChild.success) {
        return addressResultChild
      }

      // ─────────────────────────────
      // Parent saving state
      // ─────────────────────────────

      dispatch(
        setParentSaving({
          parentId,
          saving: true,
        }),
      )

      // ─────────────────────────────
      // Save Parent
      // ─────────────────────────────

      const parentPayload = mapParentToPayload(
        {...parent.values, ...addressResultParent.resolvedAddress},
        addressResultParent.timeZone,
      )

      const parentResponse = await run(
        client('multi-location/parent', {
          method: 'POST',
          data: parentPayload,
        }),
      )

      // if (!parentResponse?.ccId) {
      //   toast.error('Parent response missing required ccId')
      //   throw new Error('Parent response missing required ID')
      // }
      // toast.success('Parent saved successfully')

      // ─────────────────────────────
      // Child saving state
      // ─────────────────────────────

      dispatch(
        setChildSaving({
          parentId,
          childId,
          saving: true,
        }),
      )

      // ─────────────────────────────
      // Save First Child
      // ─────────────────────────────

      const childPayload = mapFirstChildToPayload(
        {...child.values, ...addressResultChild.resolvedAddress},
        parentResponse,
        addressResultChild.timeZone,
        parent.values,
      )

      const childResponse = await run(
        client('multi-location/location', {
          method: 'POST',
          data: childPayload,
        }),
      )

      // ─────────────────────────────
      // Redux Updates
      // ─────────────────────────────
      // toast.success('Location saved successfully')

      dispatch(
        setParentSaved({
          parentId,
          data: {
            saved: true,
            dirty: false,
            existing: true,
            response: parentResponse,
          },
        }),
      )

      dispatch(
        setChildSaved({
          parentId,
          childId,
          data: {
            saved: true,
            dirty: false,
            existing: true,
            response: childResponse,
          },
        }),
      )

      dispatch(setActiveAccordion(null))

      return {
        success: true,
        parentResponse,
        childResponse,
      }
    } catch (error) {
      console.error(error)

      return {
        success: false,
        error,
      }
    } finally {
      dispatch(
        setParentSaving({
          parentId: parent?.id,
          saving: false,
        }),
      )

      dispatch(
        setChildSaving({
          parentId: parent?.id,
          childId: parent?.children?.[0]?.id,
          saving: false,
        }),
      )
    }
  }

  // ─────────────────────────────────────────
  // Save Child
  // ─────────────────────────────────────────

  // Dead/superseded by saveSingleLocChild (not returned from this hook). Kept for
  // now; eslint-disabled so the build compiles.
  // eslint-disable-next-line no-unused-vars
  const saveChild = async (parentId, childId) => {
    const parent = parents.find(p => p.id === parentId)

    if (!parent) return

    const child = parent.children.find(c => c.id === childId)

    if (!child) return

    const values = child.values

    const fieldConfigMap = getFieldConfigMap(child)

    const errors = validateLocation(child.values, fieldConfigMap)

    if (Object.keys(errors).length) {
      dispatch(
        setChildErrors({
          parentId,
          childId,
          errors,
        }),
      )

      toast.error('Please complete all required fields before saving.')

      return {success: false, errors}
    }

    const addressResult = await handleAddressValidation({
      account: child,
      parentId,
      childId: childId,
      isChild: true,
    })

    if (!addressResult.success) {
      return addressResult
    }

    try {
      dispatch(
        setChildSaving({
          parentId,
          childId,
          saving: true,
        }),
      )

      const payload = mapChildToPayload(
        {...values, ...addressResult.resolvedAddress},
        parent,
        addressResult.timeZone,
      )

      const childResponse = await run(
        client('multi-location/location', {
          method: 'POST',
          data: payload,
        }),
      )
      // toast.success('Location saved successfully')

      // ─────────────────────────────
      // Redux Updates
      // ─────────────────────────────

      dispatch(
        setChildSaved({
          parentId,
          childId,
          data: {
            saved: true,
            dirty: false,
            existing: true,
            response: childResponse,
          },
        }),
      )

      dispatch(setActiveAccordion(null))

      return {
        success: true,
        childResponse,
      }
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(
        setChildSaving({
          parentId,
          childId,
          saving: false,
        }),
      )
    }
  }

  // ─────────────────────────────────────────
  // Return
  // ─────────────────────────────────────────

  return {
    verifiedEmail,

    selectedTransactionType,

    selectedCountry,

    isMultiLocation,

    parents,

    activeAccordion,

    setActiveAccordion: id => dispatch(setActiveAccordion(id)),

    isAddressValidating,

    addressSuggestion,

    resolveAddressSuggestion,

    addChild,

    validateParentField,

    validateChildField,

    updateParent,

    updateChild,

    saveSingleLocParent,

    saveParentWithFirstChild,

    saveChild,

    discardChild,

    handleTransactionTypeChange,

    handleCountryandRegionChange,
  }
}
